import React, { useEffect, useState, useMemo, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { debounce } from "lodash";
import { Table, Input, Button, Modal } from "antd";

import { errEnum, PAGE_LENGTH } from "../../config";
import defaultLogo from "../../assets/images/default_company_logo.jpg";
import popupImg from "../../assets/images/free-user-login-prompt.jpg.jpeg";
import subscribepopupImg from "../../assets/images/subscribe-now-prompt-img.jpg";
import TrialModal from "../../common/TrialModal";
import {
  getCompanyList,
  savePaginationValues,
  saveAdvancedSelectedFilters,
  selectedRow,
  saveSearchAction,
  resetCompanySavedSearch,
  emptyErrorObj,
} from "../../actionCreator/companyListingActionCreater";
import Loader from "../loader";
import { getUserInfo, getPartialPhoneNumber } from "../../utils/utils";
import { useExport } from "./hooks/useExport";
import TagCompany from "./TagCompany";
import { useAlert } from "../../context/AlertProvider";
import { AuthContext } from "../../context/AuthContext";
import FeatureComingSoon from "../../common/FeatureComingSoon";

const CompanyContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { isAuthenticated: isLoggedIn } = useContext(AuthContext);
  const { onExport } = useExport();
  const [companyList, setCompanyList] = useState();
  const [showModal, setShowModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [openModal, setOpenModal] = useState({
    info: null,
    open: false,
  });
  const [searchValues, setSearchValues] = useState({
    tagname: "",
    description: "",
    tagError: "",
  });
  const errObj = useSelector((state) => state.companyListingReducer.errObj);
  const companyFilterList = useSelector((state) => state.companyListingReducer);
  const companySelectedFilterList = useSelector(
    (state) => state.companyListingReducer.selectedFilters,
  );
  const loading = useSelector((state) => state.CommonReducer.loading);
  const paginationValue = useSelector(
    (state) => state.companyListingReducer.paginationValue,
  );
  const selectedRecords = useSelector(
    (state) => state.companyListingReducer.selectedRecords,
  );
  const topSearchValue = useSelector(
    (state) => state.HeaderReducer.topSearchValue,
  );
  const dashboardCompanySavedSearchData = useSelector(
    (state) => state.DashboardReducer.companySavedSearchData,
  );
  const saveSearchData = useSelector(
    (state) => state.companyListingReducer?.saveSearch,
  );
  const companySelectionList = useSelector(
    (state) => state.companyListingReducer?.companySelectionList,
  );

  useEffect(() => {
    if (companySelectedFilterList?.quickSelectionRange?.start) {
      let selectionKeys = [];
      companySelectionList?.forEach((record) => {
        selectionKeys.push(record);
      });
      dispatch(selectedRow(selectionKeys));
    } else {
      dispatch(selectedRow([]));
    }
  }, [companySelectionList]);

  useMemo(() => {
    const payload = {
      ...companySelectedFilterList,
      topSearchValue: topSearchValue,
    };
    dispatch(saveAdvancedSelectedFilters(payload));
    if (topSearchValue) {
      dispatch(getCompanyList(payload, paginationValue, true));
      // dispatch(topSearch(""));
    }
  }, [topSearchValue]); //companySelectedFilterList

  const renderSocialLinks = (socialLinks) => {
    return socialLinks?.map((link) => {
      if (link?.name === "facebook") {
        return (
          <Link to={link?.proifileUrl} target="_blank">
            <i className="lab fs-20 facebook lab la-facebook"></i>
          </Link>
        );
      } else if (link?.name === "Linkedin") {
        return (
          <Link to={link?.proifileUrl} target="_blank">
            <i className="lab fs-20 linkedin  lab la-linkedin"></i>
          </Link>
        );
      } else if (link?.name === "twitter") {
        return (
          <Link to={link?.proifileUrl} target="_blank">
            <i className="lab fs-20  twitter la la-twitter-square"></i>
          </Link>
        );
      }
    });
  };

  useEffect(() => {
    if (!companyFilterList?.companyList) return;
    const data =
      companyFilterList?.companyList?.map((record) => ({
        key: record.id,
        name: record,
        industry: record?.industry?.name,
        location: `${record?.city ? record.city + ", " : ""}${record?.state || ""}`,
        totalEmployees: record?.totalEmployees,
        phone: record.phoneNo,
        social: renderSocialLinks(record?.socialLinks),
      })) || [];

    setCompanyList(data);
  }, [companyFilterList]);

  const rowSelection = {
    onSelect: (record, selected, selectedRows) => {
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => d !== record.key,
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, record.key]));
      }
    },
    onSelectAll: (selected, selectedRows, changeRows) => {
      const keys = changeRows?.map((d) => d.key);
      if (!selected) {
        const filteredRecords = selectedRecords?.filter(
          (d) => !keys.includes(d),
        );
        dispatch(selectedRow(filteredRecords));
      } else {
        dispatch(selectedRow([...selectedRecords, ...keys]));
      }
    },
    getCheckboxProps: (record) => {
      return {
        disabled: record.name === "Disabled User", // Column configuration not to be checked
        name: record.name,
      };
    },
    selectedRowKeys: selectedRecords,
  };

  const checkLoginStatus = () => {
    if (isLoggedIn) {
      setShowModal(false);
    } else {
      setShowModal(true);
    }
  };

  const onSearch = debounce((event) => {
    const payload = {
      ...companySelectedFilterList,
      searchKeyword: event?.target?.value,
    };
    dispatch(saveAdvancedSelectedFilters(payload));
    dispatch(getCompanyList(payload, paginationValue));
  }, 500);

  const onPageChange = (page, pageSize) => {
    checkLoginStatus();
    if (!isLoggedIn) {
      if (page > 1 || pageSize > PAGE_LENGTH)
        setOpenModal({ info: null, open: true });
      setShowModal(false);
      return false;
    } else {
      const pageValues = {
        start: page - 1,
        end: pageSize,
      };
      let payload = {
        ...companySelectedFilterList,
        pageSize,
      };

      if (topSearchValue) {
        payload = {
          ...payload,
          topSearchValue: topSearchValue,
        };
      }
      dispatch(saveAdvancedSelectedFilters(payload));
      //in case of saved search in reducer it will discard other filters
      //searchPayload = JSON.parse(newList[0].dataDump);
      dispatch(savePaginationValues(pageValues));
      let searchPayload = {};
      if (Object.keys(companySelectedFilterList?.selectedSavedSearch).length) {
        searchPayload = JSON.parse(
          companySelectedFilterList?.selectedSavedSearch[0]?.dataDump,
        );
        dispatch(getCompanyList(searchPayload, pageValues));
      } else {
        dispatch(getCompanyList(payload, pageValues));
      }
      //console.log(companySelectedFilterList?.selectedSavedSearch,'jhjgjgjh',searchPayload)
    }
  };

  const getDetails = (id, name) => {
    let cname = name.replaceAll(" ", "-").toLowerCase();
    navigate(`/company-summary/${id}/${cname}`);
  };

  const onHandleSaveSearch = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      setShowModal(true);
    } else {
      //setShowModal(false);
    }
  };

  const checkFilters = () => {
    let hasFilters = true;

    Object.keys(companySelectedFilterList).forEach((item) => {
      if (companySelectedFilterList[item]?.length) {
        hasFilters = false;
      }
    });
    return hasFilters;
  };

  const closeModal = () => {
    setShowModal(false);
    setOpenModal(false);
  };

  const onConfrim = () => {
    if (!searchValues.tagname) {
      setSearchValues({
        ...searchValues,
        tagError: "error",
      });
    } else {
      const { id, email, login } = getUserInfo();
      const payload = {
        accountId: login,
        dataDump: JSON.stringify(companySelectedFilterList),
        fullName: searchValues.tagname,
        emailId: email,
        source: "Company",
        userId: id,
      };
      dispatch(saveSearchAction(payload));
      setShowModal(false);
    }
  };

  const onInputChange = (e) => {
    setSearchValues({
      ...searchValues,
      [e.target.name]: e.target.value,
    });
  };

  const redirectToSignup = () => {
    setShowModal(false);
    navigate("/signup");
  };

  const [successMsg, setSuccessMsg] = useState("");

  const [saveCompanySearchSuccess, setSaveCompanySearchSuccess] =
    useState(false);
  useEffect(() => {
    if (Object.keys(saveSearchData).length) {
      setSaveCompanySearchSuccess(true);
      setSuccessMsg("Saved Search Created!");
    }
  }, [saveSearchData]);

  const closeCompanySuccessModal = () => {
    setSaveCompanySearchSuccess(false);
    dispatch(resetCompanySavedSearch());
  };

  const tagPage = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      if (selectedRecords.length) {
        setShowTagModal(true);
      } else {
        showAlert("warning", "please select record.");
      }
    }
  };

  const closeTagModal = () => {
    setShowTagModal(false);
  };

  const printPage = () => {
    checkLoginStatus();
    if (isLoggedIn) {
      window.print();
    }
  };

  const { Search } = Input;
  const columns = [
    {
      title: <div className="companyname">Company Name</div>,
      dataIndex: "name",
      render: (record, row) => {
        return (
          <div
            className="namecol"
            onClick={() => getDetails(row.key, record?.name)}
          >
            <div className="logo">
              <img
                src={record?.companyLogoUrl || defaultLogo}
                alt=""
                onError={(e) => {
                  e.currentTarget.src = defaultLogo;
                }}
              />
            </div>
            <span className="cname">{record?.name}</span>
          </div>
        );
      },
      fixed: "left",
    },
    {
      title: "Industry",
      dataIndex: "industry",
    },
    {
      title: "Location",
      dataIndex: "location",
      className: "location",
    },
    {
      title: "Employees",
      dataIndex: "totalEmployees",
      className: "totalEmployees",
    },
    {
      title: "Phone Number",
      dataIndex: "phone",
      render: (text, row) => {
        return getPartialPhoneNumber(text);
      },
    },
    {
      title: "Social",
      dataIndex: "social",
    },
  ];

  const currentPage = paginationValue.start + 1;
  const itemsPerPage = paginationValue.end;
  const totalItems = companyFilterList?.totalCount;
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <>
      <div id="content-wrapper" className="d-flex flex-column ">
        <div id="content" className="">
          <div className="container-fluid pl-0 pr-0">
            <div className="row">
              <div className="col-xl-12 col-lg-10 pl-0">
                <div className="card mb-4">
                  <div className="card-header py-3 f-rev d-flex align-items-center justify-content-between">
                    <div
                      className="d-flex flex-column flex-lg-row align-items-lg-center m-0 ml-2 font-weight-bold text-primary mob-t"
                      style={{ gap: 10, width: "100%" }}
                    >
                      {totalItems > 0 && (
                        <h6 className="m-0 ml-2 font-weight-bold text-black mob-t heading13px">
                          Showing {startItem}-{endItem}
                          <span className="m-1">of</span>{" "}
                          {companyFilterList?.totalCount}
                          <span className="m-1">results</span>
                        </h6>
                      )}

                      <span className="heading13px text-black">
                        {companySelectedFilterList?.quickSelectionRange && (
                          <>
                            Pages:{" "}
                            {
                              companySelectedFilterList?.quickSelectionRange
                                ?.start
                            }
                            -
                            {
                              companySelectedFilterList?.quickSelectionRange
                                ?.end
                            }
                            ;{" "}
                          </>
                        )}
                        {!!selectedRecords?.length && (
                          <span className="heading13px text-black">
                            {selectedRecords?.length} records selected
                          </span>
                        )}
                      </span>
                    </div>

                    <div className="buttons-container textsearch">
                      <ul className="d-flex mt-1 m-mt">
                        <li>
                          <a
                            className=" mr-2"
                            href="#"
                            id=""
                            role="button"
                            data-toggle=""
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            <i
                              className="right-icons las la-tags"
                              aria-hidden="true"
                              onClick={tagPage}
                            ></i>
                          </a>
                        </li>

                        <li>
                          <a
                            className=" mr-2"
                            href="#"
                            id=""
                            role="button"
                            data-toggle=""
                            aria-haspopup="true"
                            aria-expanded="false"
                          >
                            <i
                              className="right-icons la la-file-excel"
                              aria-hidden="true"
                              onClick={() => {
                                checkLoginStatus();
                                onExport(selectedRecords, "exl");
                              }}
                            ></i>
                          </a>
                        </li>

                        <li>
                          <a
                            className=" mr-2"
                            role="button"
                            data-toggle=""
                            aria-haspopup="true"
                            aria-expanded="false"
                            onClick={() => {
                              checkLoginStatus();
                              onExport(selectedRecords, "pdf");
                            }}
                          >
                            <i
                              className="right-icons la la-file-pdf"
                              aria-hidden="true"
                            ></i>
                          </a>
                        </li>

                        <li>
                          <a
                            className=" mr-2"
                            role="button"
                            data-toggle=""
                            aria-haspopup="true"
                            aria-expanded="false"
                            onClick={printPage}
                          >
                            <i
                              className="right-icons la la-print"
                              aria-hidden="true"
                            ></i>
                          </a>
                        </li>
                      </ul>
                      <Search
                        placeholder="Product & Services"
                        allowClear
                        onChange={onSearch}
                        style={{ width: 200 }}
                      />

                      <Button
                        className="d-none d-sm-inline-block ml-2 btn-outline-grey"
                        onClick={onHandleSaveSearch}
                        disabled={checkFilters()}
                        title={checkFilters() ? "Filters are not selected" : ""}
                      >
                        <i className="fas fa-save pr-1"></i> SAVE SEARCH
                      </Button>
                      <FeatureComingSoon>
                        <Button className="d-none d-sm-inline-block ml-2 btn-outline-grey">
                          <i className="fas fa-bolt pr-1"></i> CONNECT TO CRM
                        </Button>
                      </FeatureComingSoon>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="table-container text-nowrap">
                      <div className="table-wrapper">
                        {!!loading ? (
                          <Loader />
                        ) : (
                          <Table
                            rowSelection={{
                              type: "checkbox",
                              ...rowSelection,
                            }}
                            columns={columns}
                            dataSource={companyList}
                            pagination={{
                              responsive: true,
                              defaultCurrent: paginationValue?.start + 1,
                              current: paginationValue?.start + 1,
                              total: companyFilterList?.totalCount,
                              pageSize:
                                parseInt(PAGE_LENGTH) >
                                parseInt(companyFilterList?.totalCount)
                                  ? companyFilterList?.totalCount
                                  : paginationValue.end
                                    ? paginationValue.end
                                    : PAGE_LENGTH,
                              position: ["bottomCenter"],
                              onChange: onPageChange,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {saveCompanySearchSuccess ? (
        <TrialModal
          openModal={saveCompanySearchSuccess}
          closeModal={closeCompanySuccessModal}
          redirect={false}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              <p style={{ color: "#0000FF" }}>{successMsg}</p>
            </div>
          }
          modalWidth="400px"
        />
      ) : (
        ""
      )}
      {errObj[errEnum.DOWNLOAD_ERROR] && (
        <TrialModal
          openModal={!!errObj[errEnum.DOWNLOAD_ERROR]}
          closeModal={() => dispatch(emptyErrorObj())}
          buttonText="OK"
          modalBody={
            <div id="small-dialog2">
              {errObj[errEnum.DOWNLOAD_ERROR]
                ? errObj[errEnum.DOWNLOAD_ERROR].message
                : "Unable to complete the action. Please try again later."}
            </div>
          }
          modalWidth="400px"
        />
      )}
      <TagCompany
        showTagModal={showTagModal && isLoggedIn}
        selectedRecords={selectedRecords}
        closeTagModal={closeTagModal}
      />

      {showModal ? (
        isLoggedIn ? (
          <Modal
            title="Save Search"
            width="400px"
            closable={true}
            open={showModal}
            onCancel={closeModal}
            onOk={onConfrim}
          >
            <div className="pop-up errorformcontainer ">
              <div className="form">
                <div className="formcol1">
                  <label>Search Name</label>
                </div>
                <div className="formcol2">
                  <Input
                    name="tagname"
                    status={searchValues?.tagError}
                    value={searchValues.tagname}
                    placeholder="Name"
                    onChange={onInputChange}
                  />
                </div>
              </div>
            </div>
          </Modal>
        ) : (
          <TrialModal
            openModal={showModal}
            closeModal={closeModal}
            redirectToSignup={redirectToSignup}
            buttonText="Start Free Trial"
            redirect={true}
            modalBody={
              <div id="small-dialog2">
                <div align="center">
                  <img src={popupImg} alt="" />
                </div>
                <p style={{ color: "#0000FF" }}>
                  Get 10 free verified contacts with a BeyondLeadz Pro trial
                </p>
                <p>
                  BeyondLeadz Pro customers close deals faster thanks to
                  relevant
                </p>
              </div>
            }
            modalWidth="400px"
          />
        )
      ) : (
        ""
      )}

      {openModal?.open && (
        <TrialModal
          openModal={openModal}
          closeModal={closeModal}
          redirectToSignup={redirectToSignup}
          redirect={true}
          // buttonText="Start Free Trial"
          buttonText="SUBSCRIBE NOW!"
          modalBody={
            <div id="small-dialog2">
              <div align="center">
                <img src={subscribepopupImg} alt="" />
              </div>
              <p style={{ color: "#0000FF" }}>
                PLEASE SUBSCRIBE TO VIEW ALL DETAILS
              </p>
            </div>
          }
          modalWidth="400px"
        />
      )}
    </>
  );
};

export default CompanyContent;
